import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getSupportingEvidence, resolveDocumentUrl } from "../../api/documents.api";
import EvidenceDocumentViewer from "../../components/EvidenceDocumentViewer";
import SupportingEvidenceDetailDrawer from "./SupportingEvidenceDetailDrawer";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Table from "../../ui/Table";
import PageState from "../../ui/PageState";
import { toneTokens } from "../../ui/statusTokens";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";

const PAGE_SIZE = 20;

const SOURCE_MODULE_OPTIONS = [
  { value: "", label: "All source modules" },
  { value: "Risks", label: "Risks" },
  { value: "Controls", label: "Controls" },
  { value: "Incidents", label: "Incidents" },
  { value: "Audits", label: "Audits" },
  { value: "Board Packs", label: "Board Packs" },
  { value: "Policies", label: "Policies" },
  { value: "Actions", label: "Actions" },
  { value: "Assessments", label: "Assessments" },
];

const STORAGE_TYPE_OPTIONS = [
  { value: "", label: "All storage types" },
  { value: "file", label: "File" },
  { value: "url", label: "Link" },
  { value: "metadata", label: "Record" },
  { value: "generated", label: "Generated document" },
];

const PARENT_ENTITY_OPTIONS = [
  { value: "", label: "All parent entity types" },
  { value: "risk", label: "Risk" },
  { value: "control", label: "Control" },
  { value: "incident", label: "Incident" },
  { value: "audit", label: "Audit" },
  { value: "board_pack", label: "Board pack" },
  { value: "tenant_policy", label: "Policy" },
  { value: "action", label: "Action" },
  { value: "assessment", label: "Assessment" },
];

const STORAGE_TYPE_LABELS = {
  file: "File",
  url: "Link",
  metadata: "Record",
  generated: "Generated document",
};

const PARENT_ENTITY_LABELS = {
  risk: "Risk",
  control: "Control",
  incident: "Incident",
  audit: "Audit",
  board_pack: "Board pack",
  tenant_policy: "Policy",
  action: "Action",
  assessment: "Assessment",
};

const PREFERRED_LIFECYCLE_ORDER = [
  "active",
  "uploaded",
  "approved",
  "published",
  "draft",
  "pending_upload",
  "scanning",
  "quarantined",
];

const pageStyle = {
  display: "grid",
  gap: spacing.lg,
};

const heroStyle = {
  background: "linear-gradient(135deg, rgba(15, 118, 110, 0.12) 0%, rgba(8, 145, 178, 0.08) 100%)",
  border: `1px solid ${colors.accentBorder}`,
  borderRadius: radius.xl,
  padding: spacing.xl,
  boxShadow: shadows.xs,
};

const eyebrowStyle = {
  margin: 0,
  color: colors.brandPrimary,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.bold,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const titleStyle = {
  margin: `${spacing.sm}px 0 ${spacing.xs}px`,
  color: colors.text,
  fontSize: typography.size["2xl"],
  fontWeight: typography.weight.bold,
  lineHeight: typography.lineHeight.compact,
};

const subtitleStyle = {
  margin: 0,
  maxWidth: 760,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
  lineHeight: typography.lineHeight.reading,
};

const filterPanelStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xl,
  padding: spacing.lg,
  boxShadow: shadows.xs,
  display: "grid",
  gap: spacing.md,
};

const gridFiltersStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const summaryRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const summaryTextStyle = {
  color: colors.textSecondary,
  fontSize: typography.size.xs,
};

const filterSectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
};

const filterSectionDescriptionStyle = {
  margin: 0,
  color: colors.textSecondary,
  fontSize: typography.size.xs,
  lineHeight: typography.lineHeight.reading,
};

const searchRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(240px, 1fr) auto",
  gap: spacing.md,
  alignItems: "end",
};

const paginationStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const tableCaptionStyle = {
  color: colors.textSecondary,
  fontSize: typography.size.xs,
};

const actionGroupStyle = {
  display: "flex",
  gap: spacing.xs,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const actionButtonStyle = {
  minHeight: 32,
  padding: `0 ${spacing.md}px`,
};

const mutedTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const titleCellPrimaryStyle = {
  fontWeight: typography.weight.medium,
  color: colors.text,
};

const titleCellSecondaryStyle = {
  marginTop: spacing.xs,
  color: colors.textMuted,
  fontSize: typography.size.xs,
  lineHeight: typography.lineHeight.reading,
};

const tableRegionStyle = {
  overflowX: "auto",
};

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const formatLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getStorageTypeLabel = (value) => STORAGE_TYPE_LABELS[value] || formatLabel(value) || "Record";

const getLifecycleLabel = (value) => (value ? formatLabel(value) : "Not set");

const getParentEntityTypeLabel = (value) => PARENT_ENTITY_LABELS[value] || formatLabel(value) || "Record";

const getUploadedByLabel = (value) => {
  if (!value) return "Not recorded";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.display_name || value.name || value.email || "Not recorded";
  }
  return "Not recorded";
};

const getEvidenceTitle = (row) => row?.title || row?.filename || "Untitled record";

const getEvidenceCaption = (row) => {
  if (row?.filename && row.filename !== row.title) return row.filename;
  if (row?.description) return row.description;
  return null;
};

const getParentEntityText = (row) => row?.parent_entity_title || getParentEntityTypeLabel(row?.parent_entity_type);

const getBadgeToneStyle = (tone) => {
  const resolved = toneTokens[tone] || toneTokens.neutral;
  return {
    background: resolved.background,
    borderColor: resolved.border,
    color: resolved.color,
  };
};

const getSourceModuleBadgeStyle = () => getBadgeToneStyle("accent");

const getStorageTypeBadgeStyle = (value) => {
  switch (value) {
    case "file":
      return getBadgeToneStyle("accent");
    case "url":
      return getBadgeToneStyle("info");
    case "generated":
      return getBadgeToneStyle("accent");
    case "metadata":
    default:
      return getBadgeToneStyle("neutral");
  }
};

const getLifecycleBadgeStyle = (value) => {
  switch (String(value || "").trim().toLowerCase()) {
    case "active":
      return getBadgeToneStyle("accent");
    case "uploaded":
      return getBadgeToneStyle("info");
    case "approved":
    case "published":
      return getBadgeToneStyle("success");
    case "draft":
      return getBadgeToneStyle("neutral");
    case "pending_upload":
    case "scanning":
      return getBadgeToneStyle("warning");
    case "quarantined":
      return getBadgeToneStyle("danger");
    default:
      return getBadgeToneStyle("neutral");
  }
};

const buildLifecycleOptions = (items, currentValue) => {
  const discovered = new Set(PREFERRED_LIFECYCLE_ORDER);
  if (currentValue) discovered.add(currentValue);
  (items || []).forEach((item) => {
    if (item?.lifecycle_status) {
      discovered.add(item.lifecycle_status);
    }
  });

  const ordered = [
    ...PREFERRED_LIFECYCLE_ORDER.filter((value) => discovered.has(value)),
    ...Array.from(discovered).filter((value) => !PREFERRED_LIFECYCLE_ORDER.includes(value)).sort(),
  ];

  return [
    { value: "", label: "All lifecycle statuses" },
    ...ordered.map((value) => ({ value, label: formatLabel(value) })),
  ];
};

const resolveSourceRoute = (row) => {
  if (!row?.can_open_source) return null;

  const parentEntityId = row?.parent_entity_id;
  switch (row?.parent_entity_type) {
    case "risk":
      return parentEntityId != null ? `/risks/${parentEntityId}` : null;
    case "control":
      return parentEntityId != null ? `/controls/${parentEntityId}` : null;
    case "incident":
      return parentEntityId != null ? `/incidents/${parentEntityId}` : null;
    case "action":
      return parentEntityId != null ? `/actions/${parentEntityId}` : null;
    case "audit":
      return parentEntityId != null ? `/audit/audits/${parentEntityId}` : null;
    case "tenant_policy":
      return parentEntityId != null ? `/catalog/policies/${parentEntityId}` : null;
    default:
      break;
  }

  const sourceUrl = String(row?.source_detail_url || "").trim();
  if (!sourceUrl) return null;
  if (/^\/(risks|controls|incidents|actions)\//.test(sourceUrl)) {
    return sourceUrl;
  }
  const auditMatch = sourceUrl.match(/^\/audits\/([^/]+)$/);
  if (auditMatch?.[1]) {
    return `/audit/audits/${auditMatch[1]}`;
  }
  if (/^\/audit\/audits\//.test(sourceUrl)) {
    return sourceUrl;
  }
  const policyMatch = sourceUrl.match(/^\/tenant\/policies\/([^/]+)$/);
  if (policyMatch?.[1]) {
    return `/catalog/policies/${policyMatch[1]}`;
  }
  if (/^\/catalog\/policies\//.test(sourceUrl)) {
    return sourceUrl;
  }
  return null;
};

export default function SupportingEvidence() {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewerItem, setViewerItem] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    sourceModule: "",
    storageType: "",
    parentEntityType: "",
    lifecycleStatus: "",
  });

  const queryParams = useMemo(
    () => ({
      source_module: filters.sourceModule || undefined,
      storage_type: filters.storageType || undefined,
      parent_entity_type: filters.parentEntityType || undefined,
      lifecycle_status: filters.lifecycleStatus || undefined,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, offset, search]
  );

  const evidenceQuery = useQuery({
    queryKey: ["supporting-evidence", queryParams],
    queryFn: () => getSupportingEvidence(queryParams),
    keepPreviousData: true,
  });

  const items = useMemo(
    () => (Array.isArray(evidenceQuery.data?.items) ? evidenceQuery.data.items : []),
    [evidenceQuery.data?.items]
  );
  const total = Number.isFinite(evidenceQuery.data?.total)
    ? evidenceQuery.data.total
    : offset + items.length + (evidenceQuery.data?.next_cursor ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(Math.max(total, 1) / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasNextPage = Boolean(evidenceQuery.data?.next_cursor) || offset + PAGE_SIZE < total;
  const lifecycleOptions = useMemo(
    () => buildLifecycleOptions(items, filters.lifecycleStatus),
    [filters.lifecycleStatus, items]
  );

  useEffect(() => {
    if (!selectedItem) return;
    const nextSelectedItem = items.find((item) => item?.id === selectedItem?.id);
    if (nextSelectedItem) {
      if (nextSelectedItem !== selectedItem) {
        setSelectedItem(nextSelectedItem);
      }
      return;
    }
    setSelectedItem(null);
  }, [items, selectedItem]);

  const handleFilterChange = (key) => (event) => {
    const { value } = event.target;
    setOffset(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setOffset(0);
    setFilters({
      sourceModule: "",
      storageType: "",
      parentEntityType: "",
      lifecycleStatus: "",
    });
  };

  const openDestination = (value, { external = false } = {}) => {
    if (!value) return;
    if (external || isAbsoluteUrl(value)) {
      window.open(value, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(value);
  };

  const openDetails = (row) => {
    if (!row) return;
    setSelectedItem(row);
  };

  const canViewDocument = (row) => Boolean(row?.can_download && row?.download_url);

  const renderActions = (row) => {
    const actions = [];
    const itemTitle = getEvidenceTitle(row);
    const sourceRoute = resolveSourceRoute(row);

    actions.push(
      <Button
        key="details"
        type="button"
        variant="ghost"
        style={actionButtonStyle}
        aria-label={`View details for ${itemTitle}`}
        onClick={(event) => {
          event.stopPropagation();
          openDetails(row);
        }}
      >
        Details
      </Button>
    );

    if (canViewDocument(row)) {
      actions.push(
        <Button
          key="view"
          type="button"
          variant="primary"
          style={actionButtonStyle}
          aria-label={`View ${itemTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            setViewerItem(row);
          }}
        >
          View
        </Button>
      );
    }

    if (row?.can_download && row?.download_url) {
      actions.push(
        <Button
          key="download"
          type="button"
          variant="secondary"
          style={actionButtonStyle}
          aria-label={`Download ${itemTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            openDestination(resolveDocumentUrl(row.download_url), { external: true });
          }}
        >
          Download
        </Button>
      );
    }

    if (row?.storage_type === "url" && row?.url) {
      actions.push(
        <Button
          key="open-link"
          type="button"
          variant="secondary"
          style={actionButtonStyle}
          aria-label={`Open link for ${itemTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            openDestination(row.url, { external: true });
          }}
        >
          Open link
        </Button>
      );
    }

    if (sourceRoute) {
      actions.push(
        <Button
          key="open-source"
          type="button"
          variant="ghost"
          style={actionButtonStyle}
          aria-label={`Open source for ${itemTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            openDestination(sourceRoute);
          }}
        >
          Open source
        </Button>
      );
    }

    if (actions.length === 0) {
      return null;
    }

    return <div style={actionGroupStyle} role="group" aria-label={`Actions for ${itemTitle}`}>{actions}</div>;
  };

  const columns = [
    {
      id: "title",
      header: "Title or filename",
      render: (row) => (
        <div>
          <div style={titleCellPrimaryStyle}>{getEvidenceTitle(row)}</div>
          {getEvidenceCaption(row) ? (
            <div style={titleCellSecondaryStyle}>{getEvidenceCaption(row)}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: "source_module",
      header: "Source module",
      render: (row) => (
        <div>
          <StatusBadge
            value="active"
            label={row?.source_module || "Unknown module"}
            aria-label={`Source module ${row?.source_module || "Unknown module"}`}
            style={getSourceModuleBadgeStyle()}
          />
          {row?.source_type ? (
            <div style={titleCellSecondaryStyle}>{formatLabel(row.source_type)}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: "parent_entity",
      header: "Parent entity",
      render: (row) => (
        <div>
          <div style={titleCellPrimaryStyle}>{getParentEntityText(row)}</div>
          <div style={titleCellSecondaryStyle}>{getParentEntityTypeLabel(row?.parent_entity_type)}</div>
        </div>
      ),
    },
    {
      id: "storage_type",
      header: "Storage type",
      render: (row) => (
        <StatusBadge
          value="active"
          label={getStorageTypeLabel(row?.storage_type)}
          aria-label={`Storage type ${getStorageTypeLabel(row?.storage_type)}`}
          style={getStorageTypeBadgeStyle(row?.storage_type)}
        />
      ),
    },
    {
      id: "lifecycle_status",
      header: "Lifecycle status",
      render: (row) =>
        row?.lifecycle_status ? (
          <StatusBadge
            value="active"
            label={getLifecycleLabel(row.lifecycle_status)}
            aria-label={`Lifecycle status ${getLifecycleLabel(row.lifecycle_status)}`}
            style={getLifecycleBadgeStyle(row.lifecycle_status)}
          />
        ) : (
          <span style={mutedTextStyle}>Not set</span>
        ),
    },
    {
      id: "created_at",
      header: "Created or uploaded",
      render: (row) => formatDate(row?.created_at),
    },
    {
      id: "uploaded_by",
      header: "Uploaded by",
      render: (row) => getUploadedByLabel(row?.uploaded_by),
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      render: (row) => renderActions(row),
    },
  ];

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(searchInput) ||
    Boolean(filters.sourceModule) ||
    Boolean(filters.storageType) ||
    Boolean(filters.parentEntityType) ||
    Boolean(filters.lifecycleStatus);

  return (
    <div style={pageStyle}>
      <section style={heroStyle}>
        <p style={eyebrowStyle}>Documents</p>
        <h1 style={titleStyle}>Supporting Evidence</h1>
        <p style={subtitleStyle}>View evidence, documents, links and generated records from across Glyder in one place.</p>
      </section>

      <section style={filterPanelStyle}>
        <div style={{ display: "grid", gap: spacing.xs }}>
          <h2 style={filterSectionTitleStyle}>Refine results</h2>
          <p style={filterSectionDescriptionStyle}>Search and filter supporting evidence across the documents workspace.</p>
        </div>

        <form onSubmit={handleSearchSubmit} style={searchRowStyle}>
          <div style={{ minWidth: 0 }}>
            <Input
              label="Search"
              placeholder="Search supporting evidence"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
            <Button type="submit" variant="primary">
              Search
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters} disabled={!hasActiveFilters}>
              Clear filters
            </Button>
          </div>
        </form>

        <div style={gridFiltersStyle}>
          <Select
            label="Source module"
            ariaLabel="Source module"
            value={filters.sourceModule}
            onChange={handleFilterChange("sourceModule")}
            options={SOURCE_MODULE_OPTIONS}
          />
          <Select
            label="Storage type"
            ariaLabel="Storage type"
            value={filters.storageType}
            onChange={handleFilterChange("storageType")}
            options={STORAGE_TYPE_OPTIONS}
          />
          <Select
            label="Parent entity type"
            ariaLabel="Parent entity type"
            value={filters.parentEntityType}
            onChange={handleFilterChange("parentEntityType")}
            options={PARENT_ENTITY_OPTIONS}
          />
          <Select
            label="Lifecycle status"
            ariaLabel="Lifecycle status"
            value={filters.lifecycleStatus}
            onChange={handleFilterChange("lifecycleStatus")}
            options={lifecycleOptions}
          />
        </div>
      </section>

      <section style={{ display: "grid", gap: spacing.md }}>
        <div style={summaryRowStyle}>
          <div style={summaryTextStyle} role="status" aria-live="polite">
            {evidenceQuery.isLoading
              ? "Gathering supporting evidence..."
              : `${items.length} record${items.length === 1 ? "" : "s"} shown${total ? ` of ${total}` : ""}`}
          </div>
          <div style={tableCaptionStyle}>Files, links, records and generated documents appear here when available.</div>
        </div>

        {evidenceQuery.isLoading ? (
          <PageState
            state="loading"
            title="Loading supporting evidence"
            description="Glyder is gathering supporting evidence from across the platform."
          />
        ) : evidenceQuery.isError ? (
          <PageState
            state="error"
            title="We could not load supporting evidence"
            description="Refresh and try again. If the problem continues, check your access or try again shortly."
          />
        ) : items.length === 0 ? (
          <PageState
            state="empty"
            title="No supporting evidence found yet."
            description="Evidence uploaded or linked in Risks, Controls, Incidents, Audits, Policies, Board Packs, Actions and Assessments will appear here when available."
          />
        ) : (
          <>
            <div role="region" aria-label="Supporting evidence results" style={tableRegionStyle}>
              <Table
                columns={columns}
                data={items}
                getRowKey={(row) => row.id}
                onRowClick={openDetails}
                getRowProps={(row) => ({
                  "data-testid": `supporting-evidence-row-${row.id}`,
                  "aria-label": `Open details for ${getEvidenceTitle(row)}`,
                  style: { cursor: "pointer" },
                })}
                style={{ overflowX: "auto" }}
                tableStyle={{ minWidth: 1120 }}
              />
            </div>

            <div style={paginationStyle}>
              <div style={summaryTextStyle}>
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: "flex", gap: spacing.sm }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
                  disabled={offset <= 0}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setOffset((current) => current + PAGE_SIZE)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </section>
      <SupportingEvidenceDetailDrawer
        item={selectedItem}
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        canViewDocument={canViewDocument(selectedItem)}
        onViewDocument={() => setViewerItem(selectedItem)}
        onDownload={() => openDestination(resolveDocumentUrl(selectedItem?.download_url), { external: true })}
        onOpenLink={() => openDestination(selectedItem?.url, { external: true })}
        canOpenSource={Boolean(resolveSourceRoute(selectedItem))}
        onOpenSource={() => openDestination(resolveSourceRoute(selectedItem))}
      />
      <EvidenceDocumentViewer
        open={Boolean(viewerItem)}
        onClose={() => setViewerItem(null)}
        title={getEvidenceTitle(viewerItem)}
        filename={viewerItem?.filename}
        mimeType={viewerItem?.mime_type}
        sizeBytes={viewerItem?.size_bytes}
        downloadUrl={viewerItem?.download_url}
      />
    </div>
  );
}
